// 404 PAGE
//
// Shown for any address the app does not serve: an unknown path, or a known
// public route carrying query parameters it does not accept (see
// QueryParamGuard). It is a dead end on purpose — no redirect to the landing
// page or the dashboard, so a crafted link such as
// /source=web?params-request-data=all resolves to nothing and cannot be passed
// off as a working part of the product.
//
// Nothing about the request is echoed back: the address that failed is not
// printed, and the page looks the same whether or not the visitor is signed in.

import React from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import Card from '@components/common/organisms/AppCard';
import Button from '@components/common/atoms/AppButton';
import Icon from '@components/common/atoms/Icon';
import { APP_NAME } from '@/config/appConfig';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Signed-in visitors are offered their dashboard, everyone else the public
  // home page — a link they choose to follow, never an automatic redirect.
  const homePath = user ? '/dashboard' : '/';
  const homeLabel = user ? 'Go to Dashboard' : 'Go to Home';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-6">
      <Card className="w-full max-w-lg p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center mx-auto mb-6">
          <Icon name="SearchX" size={40} className="text-indigo-600 dark:text-indigo-400" />
        </div>

        <p className="text-sm font-semibold tracking-widest text-indigo-600 dark:text-indigo-400 mb-2">
          ERROR 404
        </p>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          Page not found
        </h1>

        <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
          The address you entered doesn't exist on {APP_NAME}. Check the link and
          try again, or head back using the button below.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="flex items-center justify-center gap-2"
          >
            <Icon name="ArrowLeft" size={18} />
            Go Back
          </Button>
          <Button
            onClick={() => navigate(homePath, { replace: true })}
            className="flex items-center justify-center gap-2"
          >
            <Icon name="Home" size={18} />
            {homeLabel}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default NotFoundPage;
